import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';

function OrderManagement() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New Order Modal State
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [targetFacilityId, setTargetFacilityId] = useState('');
  const [campTitle, setCampTitle] = useState('');
  const [campMessage, setCampMessage] = useState('');
  const [campImageUrl, setCampImageUrl] = useState('');
  const [campPlacementSlot, setCampPlacementSlot] = useState('HOME_HERO_BANNER');
  const [campCtaLabel, setCampCtaLabel] = useState('Explore Now');
  const [campCtaUrl, setCampCtaUrl] = useState('');

  // Payment Confirmation Modal State
  const [showPayModal, setShowPayModal] = useState(false);
  const [payOrderId, setPayOrderId] = useState(null);
  const [payRef, setPayRef] = useState('');

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, aRes, pRes, fRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/commercial/orders`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/admin/commercial/accounts`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/admin/commercial/packages`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/facilities`, { headers: getAuthHeaders() })
      ]);
      if (oRes.ok) setOrders(await oRes.json());
      if (aRes.ok) setAccounts(await aRes.json());
      if (pRes.ok) setPackages(await pRes.json());
      if (fRes.ok) setFacilities(await fRes.json());
      setError(null);
    } catch (e) {
      setError('Failed to fetch commercial orders');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!selectedAccountId || !selectedPackageId) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          account_id: selectedAccountId,
          package_id: selectedPackageId,
          target_facility_id: targetFacilityId || null,
          campaign_title: campTitle || null,
          campaign_message: campMessage || null,
          campaign_image_url: campImageUrl || null,
          campaign_placement_slot: campPlacementSlot,
          campaign_cta_label: campCtaLabel || null,
          campaign_cta_target_url: campCtaUrl || null
        })
      });

      if (res.ok) {
        setShowOrderModal(false);
        setCampTitle('');
        setCampMessage('');
        setCampImageUrl('');
        setCampCtaUrl('');
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create commercial order');
      }
    } catch (e) {
      alert('Network error creating commercial order');
    }
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (!payOrderId || !payRef) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/orders/${payOrderId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: 'PAID',
          payment_reference: payRef
        })
      });
      if (res.ok) {
        setShowPayModal(false);
        setPayOrderId(null);
        setPayRef('');
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update order status');
      }
    } catch (e) {
      alert('Network error confirming payment');
    }
  };

  const handleFulfillOrder = async (orderId) => {
    if (!window.confirm('Fulfill this PAID order? This will generate the tax invoice, post ledger credit, and create a campaign in PENDING_APPROVAL status.')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/orders/${orderId}/fulfill`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        fetchData();
        alert('Order fulfilled successfully! Campaign created and queued for approval.');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to fulfill order');
      }
    } catch (e) {
      alert('Network error fulfilling order');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/commercial/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to update status');
      }
    } catch (e) {
      alert('Network error updating status');
    }
  };

  const formatRupees = (paise) => {
    if (paise === undefined || paise === null) return '₹0';
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <div className="card"><p>Loading Commercial Orders...</p></div>;
  }

  return (
    <div>
      {error && (
        <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Header Actions */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>Commercial Order Operations</h3>
            <p style={{ color: '#666', marginTop: '4px' }}>Draft commercial bookings, record offline payments, and trigger order fulfillment.</p>
          </div>
          <button className="btn-primary" onClick={() => setShowOrderModal(true)}>+ Create Commercial Order</button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        {orders.length === 0 ? (
          <p style={{ padding: '16px', color: '#666' }}>No commercial orders found.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Advertiser</th>
                <th>Subtotal</th>
                <th>18% GST</th>
                <th>Total (Paise)</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.order_number}</strong></td>
                  <td>{o.account_legal_name || 'Advertiser'}</td>
                  <td>{formatRupees(o.subtotal_amount)}</td>
                  <td>{formatRupees(o.tax_amount)}</td>
                  <td><strong>{formatRupees(o.total_amount)}</strong></td>
                  <td>
                    <span className={`badge-${
                      o.status === 'FULFILLED' ? 'success' :
                      o.status === 'PAID' ? 'success' :
                      o.status === 'PENDING_PAYMENT' ? 'danger' : 'info'
                    }`}>
                      {o.status}
                    </span>
                  </td>
                  <td>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td>
                    {o.status === 'DRAFT' && (
                      <button className="btn-outline" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => handleStatusChange(o.id, 'PENDING_PAYMENT')}>
                        Request Payment
                      </button>
                    )}
                    {o.status === 'PENDING_PAYMENT' && (
                      <button className="btn-primary" style={{ padding: '4px 8px', fontSize: '12px', background: '#2E7D32' }} onClick={() => { setPayOrderId(o.id); setShowPayModal(true); }}>
                        Confirm Payment
                      </button>
                    )}
                    {o.status === 'PAID' && (
                      <button className="btn-primary" style={{ padding: '4px 8px', fontSize: '12px', background: '#6A1B9A' }} onClick={() => handleFulfillOrder(o.id)}>
                        ⚡ Fulfill Order
                      </button>
                    )}
                    {o.status === 'FULFILLED' && (
                      <span style={{ fontSize: '12px', color: '#2E7D32', fontWeight: 'bold' }}>✓ Fulfilled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Order Modal */}
      {showOrderModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '640px', background: '#fff', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '16px' }}>Create Commercial Order</h3>
            <form onSubmit={handleCreateOrder}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Select Advertiser Account *</label>
                  <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                    <option value="">Select Advertiser...</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.legal_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Select Commercial Package *</label>
                  <select value={selectedPackageId} onChange={(e) => setSelectedPackageId(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                    <option value="">Select Package...</option>
                    {packages.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Target Facility (Optional)</label>
                <select value={targetFacilityId} onChange={(e) => setTargetFacilityId(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                  <option value="">None / General Placement</option>
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>{f.name} ({f.facility_type})</option>
                  ))}
                </select>
              </div>

              <h4 style={{ margin: '16px 0 8px 0', borderBottom: '1px solid #eee', paddingBottom: '4px', color: '#FF5722' }}>Campaign Creative Specs</h4>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Campaign Headline Title</label>
                <input type="text" placeholder="e.g. Special Chhath Puja Offer" value={campTitle} onChange={(e) => setCampTitle(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Banner Image URL</label>
                <input type="text" placeholder="https://example.com/banner.jpg" value={campImageUrl} onChange={(e) => setCampImageUrl(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>CTA Button Label</label>
                  <input type="text" placeholder="e.g. Call Now / Book Room" value={campCtaLabel} onChange={(e) => setCampCtaLabel(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>CTA Target Link URL</label>
                  <input type="text" placeholder="https://dharamshala.org" value={campCtaUrl} onChange={(e) => setCampCtaUrl(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setShowOrderModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Order Draft</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '400px', background: '#fff' }}>
            <h3 style={{ marginBottom: '16px' }}>Confirm Offline Payment</h3>
            <form onSubmit={handleConfirmPayment}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Payment Ref / UTR / Check # *</label>
                <input type="text" placeholder="e.g. UPI/123456789/BANK" value={payRef} onChange={(e) => setPayRef(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-outline" onClick={() => setShowPayModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" style={{ background: '#2E7D32' }}>Mark as PAID</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderManagement;
